import { ClipboardCheck } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { registerInboxSource } from '@/lib/inbox';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { qualityKeys } from './api';
import { findingsInbox } from './inbox';
import { auditRoute, auditsRoute, findingRoute, findingsRoute, healthRoute, qualityRoute, trustRoute } from './routes';

/** Data quality (ADR-0010): findings, capability trust, AI audits, device health and installation validation. */
export const qualityModule = defineModule({
  key: 'quality',
  routes: [qualityRoute, findingsRoute, findingRoute, trustRoute, auditsRoute, auditRoute, healthRoute],
  nav: [
    {
      key: 'quality',
      label: msg('Data quality'),
      description: msg('Can we trust the data?'),
      icon: ClipboardCheck,
      to: '/quality',
      section: 'manage',
      order: 100,
      module: 'quality',
      permission: ['quality.finding.read', 'quality.installation.read'],
      match: ['/quality'],
      children: [
        { key: 'findings', label: msg('Findings'), to: '/quality/findings', permission: 'quality.finding.read' },
        { key: 'trust', label: msg('Capability trust'), to: '/quality/trust', permission: 'quality.finding.read' },
        { key: 'audits', label: msg('AI audits'), to: '/quality/audits', permission: 'quality.finding.read' },
        { key: 'devices', label: msg('Device health'), to: '/quality/devices', permission: 'quality.installation.read' },
      ],
    },
  ],
  setup() {
    registerEntityHref('quality_finding', (id) => `/quality/findings/${id}`);
    registerEntityHref('quality_audit', (id) => `/quality/audits/${id}`);
    // The attention inbox on Home: open high-severity findings, for people who may decide on them.
    registerInboxSource(findingsInbox);
    // Device page: Health replaces fleet's placeholder (same key); Installation replaces the old
    // validate-installation and installation-check pages.
    registerEntityTab('device', {
      key: 'health',
      label: msg('Health'),
      order: 30,
      module: 'quality',
      permission: 'quality.installation.read',
      component: lazy(() => import('./tabs/DeviceHealthTab')),
    });
    registerEntityTab('device', {
      key: 'installation',
      label: msg('Installation'),
      order: 40,
      module: 'quality',
      permission: 'quality.installation.read',
      component: lazy(() => import('./tabs/DeviceInstallationTab')),
    });
    registerEntityTab('vehicle', {
      key: 'data-quality',
      label: msg('Data quality'),
      order: 70,
      module: 'quality',
      permission: 'quality.finding.read',
      component: lazy(() => import('./tabs/VehicleQualityTab')),
    });
    registerInvalidation('vehicle', (n) => (n.type.startsWith('quality.') ? [qualityKeys.all] : []));
    registerInvalidation('device', (n) => (n.type.startsWith('quality.') ? [qualityKeys.all] : []));
    registerInvalidation('quality_audit', () => [qualityKeys.audits, ['quality', 'audit'], ['quality', 'overview']]);
  },
});
